import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import BotonConfirmar from "../funcionales/BotonConfirmar";
import {
  TOPE_FOTOS,
  avisoDeTope,
  cuantasEntran,
  esImagen,
  fotosDe,
  quitarFoto,
  sePuedenCargar,
  subirFotos,
} from "../../services/herramientasFotos";

/* Las fotos de UNA herramienta (P-348, Nico 08/10: «Quiero agregar fotos de
 * herramientas cuando agrego una herramienta»).
 *
 * SON OPTATIVAS. Nunca se piden y no reemplazan al dibujo: el tablero se
 * sigue armando con el `tipo`. El 14/09 se habían sacado justamente porque
 * eran obligatorias y cada alta era una sesión de fotos.
 *
 * Tres usos:
 *
 *   · `herramientaId` y `puedeEditar`: la ficha de una que ya existe. Lo que
 *     se elige se sube en el momento; quitar pregunta antes, en el mismo
 *     lugar (`BotonConfirmar`, nada de `window.confirm`).
 *   · Sin `herramientaId` (el ALTA): todavía no hay a qué colgarlas. Se
 *     juntan en `pendientes` y la ficha las sube después de guardar, cuando
 *     ya hay id. Mientras las sube avisa con `ocupado`, y en ese rato no se
 *     suma ni se saca nada: la tanda ya salió y lo que cambie se perdería.
 *   · `soloVer`: el panel del tablero. Si no tiene fotos no se dibuja nada —
 *     la herramienta ya está dibujada arriba.
 *
 * En el teléfono hay además «Sacar foto», que abre directo la cámara de atrás.
 * «Agregar fotos» deja elegir varias de la galería (y, en el teléfono, también
 * ofrece la cámara).
 */

const TACTIL = "(pointer: coarse)";
const esTactil = () =>
  typeof window !== "undefined" && !!window.matchMedia?.(TACTIL)?.matches;

/* Las vistas previas de lo elegido en el alta: una URL local por archivo,
   que se suelta cuando deja de hacer falta. */
function useVistasPrevias(archivos) {
  const urls = useMemo(() => {
    if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
      return (archivos || []).map(() => null);
    }
    return (archivos || []).map((a) => URL.createObjectURL(a));
  }, [archivos]);
  useEffect(() => () => {
    if (typeof URL?.revokeObjectURL === "function") {
      urls.forEach((u) => u && URL.revokeObjectURL(u));
    }
  }, [urls]);
  return urls;
}

/* La foto en grande, encima de todo. Con flechas si hay más de una. */
function FotoGrande({ fotos, indice, onIndice, onCerrar, rotulo, acciones }) {
  const foto = fotos[indice];
  useEffect(() => {
    const tecla = (e) => {
      if (e.key === "Escape") onCerrar();
      if (e.key === "ArrowRight" && fotos.length > 1) onIndice((indice + 1) % fotos.length);
      if (e.key === "ArrowLeft" && fotos.length > 1) {
        onIndice((indice - 1 + fotos.length) % fotos.length);
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [fotos.length, indice, onIndice, onCerrar]);
  if (!foto) return null;
  return (
    <div className="hrr-foto-grande" role="dialog" aria-label={`${rotulo}, foto ${indice + 1}`}
         onClick={(e) => { e.stopPropagation(); onCerrar(); }}>
      <figure onClick={(e) => e.stopPropagation()}>
        {foto.url
          ? <img src={foto.url} alt={`${rotulo}, foto ${indice + 1} de ${fotos.length}`} />
          : <div className="hrr-foto-sin">Sin vista previa</div>}
        <figcaption>
          <span>Foto {indice + 1} de {fotos.length}</span>
          <span className="hrr-foto-grande-acciones">
            {fotos.length > 1 && (
              <>
                <button type="button" className="hrr-btn hrr-btn--chico"
                        aria-label="Foto anterior"
                        onClick={() => onIndice((indice - 1 + fotos.length) % fotos.length)}>
                  ‹
                </button>
                <button type="button" className="hrr-btn hrr-btn--chico"
                        aria-label="Foto siguiente"
                        onClick={() => onIndice((indice + 1) % fotos.length)}>
                  ›
                </button>
              </>
            )}
            {acciones?.(foto)}
            <button type="button" className="hrr-btn hrr-btn--chico" onClick={onCerrar}>
              Cerrar
            </button>
          </span>
        </figcaption>
      </figure>
    </div>
  );
}

export default function FotosHerramienta({
  herramientaId = null,
  nombre = "",
  puedeEditar = false,
  soloVer = false,
  pendientes = [],
  onPendientes,
  recargar = 0,
  ocupado = false,
}) {
  const alta = !herramientaId && !soloVer;
  const [fotos, setFotos] = useState(alta ? [] : undefined);
  const [lectura, setLectura] = useState({ error: "", faltaTabla: false });
  const [abierta, setAbierta] = useState(null);
  const [subiendo, setSubiendo] = useState(null); // {hechas, total}
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [tactil] = useState(esTactil);
  const galeria = useRef(null);
  const camara = useRef(null);
  const rotulo = nombre || "Herramienta";

  const leer = useCallback(async () => {
    if (!herramientaId) return;
    const r = await fotosDe(herramientaId);
    setFotos(r.error ? null : r.fotos);
    setLectura({ error: r.error, faltaTabla: !!r.faltaTabla });
  }, [herramientaId]);

  useEffect(() => {
    let vivo = true;
    if (!herramientaId) {
      setFotos(alta ? [] : undefined);
      /* El alta no lee nada, así que no se enteraría de que la base todavía
         no tiene dónde guardarlas (el Hub se publicó antes): lo pregunta. */
      if (alta) {
        sePuedenCargar().then((si) => {
          if (vivo && !si) setLectura({ error: "", faltaTabla: true });
        });
      }
      return () => { vivo = false; };
    }
    fotosDe(herramientaId).then((r) => {
      if (!vivo) return;
      setFotos(r.error ? null : r.fotos);
      setLectura({ error: r.error, faltaTabla: !!r.faltaTabla });
    });
    return () => { vivo = false; };
  }, [herramientaId, alta, recargar]);

  const vistas = useVistasPrevias(alta ? pendientes : []);
  const listaAlta = useMemo(
    () => pendientes.map((a, i) => ({ id: `p${i}`, url: vistas[i], archivo: a })),
    [pendientes, vistas]);
  const lista = alta ? listaAlta : (fotos || []);
  const vivas = lista.length;
  const lleno = vivas >= TOPE_FOTOS;

  /* ── Lo que se eligió ───────────────────────────────────────────────── */
  const elegidas = async (e) => {
    const archivos = Array.from(e.target.files || []);
    e.target.value = "";
    if (!archivos.length) return;
    setError("");
    setAviso("");
    const fotosNuevas = archivos.filter(esImagen);
    const noSon = archivos.length - fotosNuevas.length;
    const avisoNoSon = noSon
      ? (noSon === 1 ? "Un archivo no era una foto y no se sumó." : `${noSon} archivos no eran fotos y no se sumaron.`)
      : "";

    if (alta) {
      const { entran, sobran } = cuantasEntran(pendientes.length, fotosNuevas.length);
      onPendientes?.([...pendientes, ...fotosNuevas.slice(0, entran)]);
      setAviso([avisoDeTope(sobran), avisoNoSon].filter(Boolean).join(" "));
      return;
    }

    if (!fotosNuevas.length) { setAviso(avisoNoSon); return; }
    setSubiendo({ hechas: 0, total: fotosNuevas.length });
    const r = await subirFotos(herramientaId, fotosNuevas, {
      alProgreso: (hechas, total) => setSubiendo({ hechas, total }),
    });
    setSubiendo(null);
    if (!r.ok) setError(r.error);
    setAviso([r.aviso, avisoNoSon].filter(Boolean).join(" "));
    await leer();
  };

  const quitar = async (foto) => {
    setError("");
    setAviso("");
    if (alta) {
      onPendientes?.(pendientes.filter((a) => a !== foto.archivo));
      setAbierta(null);
      return;
    }
    const r = await quitarFoto(foto.id);
    if (!r.ok) { setError(r.error); return; }
    setAbierta(null);
    await leer();
  };

  /* ── En el panel del tablero: sólo si hay ───────────────────────────── */
  if (soloVer) {
    if (fotos === null && !lectura.faltaTabla) {
      return <p className="hrr-codigo hrr-fotos-error">No se pudieron cargar las fotos.</p>;
    }
    if (!fotos || !fotos.length) return null;
  }

  const editable = puedeEditar && !soloVer && !lectura.faltaTabla;

  return (
    <section className={`hrr-fotos${soloVer ? " hrr-fotos--ver" : ""}`}
             aria-label={soloVer ? "Fotos" : "Fotos (optativas)"}>
      {soloVer
        ? <h3 className="hrr-fotos-titulo">Fotos</h3>
        : (
          <div className="hrr-fotos-titulo">
            Fotos <span className="hrr-codigo">(optativas)</span>
          </div>
        )}

      {!soloVer && !lectura.faltaTabla && (
        <span className="hrr-codigo">
          {alta
            ? "Si querés, sumale fotos: se suben cuando guardes la herramienta. El dibujo del tablero no cambia."
            : "Para reconocer esta herramienta. Se guardan apenas las elegís; el dibujo del tablero no cambia."}
        </span>
      )}

      {fotos === undefined && <p className="hrr-codigo">Buscando…</p>}
      {fotos === null && !lectura.faltaTabla && (
        <p className="hrr-codigo">No se pudieron cargar las fotos.</p>
      )}
      {lectura.faltaTabla && !soloVer && (
        <p className="hrr-codigo">Las fotos todavía no se pueden cargar desde acá.</p>
      )}

      {lista.length > 0 && (
        <ul className="hrr-fotos-lista">
          {lista.map((f, i) => (
            <li key={f.id}>
              <button type="button" className="hrr-foto-mini"
                      aria-label={`Ver foto ${i + 1} de ${rotulo}`}
                      onClick={() => setAbierta(i)}>
                {f.url
                  ? <img src={f.url} alt="" loading="lazy" />
                  : <span>Foto {i + 1}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!soloVer && lista.length > 0 && editable && (
        <span className="hrr-codigo">
          Tocá una foto para verla grande{alta ? " o sacarla de la lista" : " o quitarla"}.
        </span>
      )}

      {editable && (
        <div className="hrr-fotos-botones">
          {lleno ? (
            <span className="hrr-codigo" role="status">
              Tiene {TOPE_FOTOS} fotos, que es el máximo. Para sumar otra, quitá alguna.
            </span>
          ) : (
            <>
              {tactil && (
                <button type="button" className="hrr-btn" disabled={!!subiendo || ocupado}
                        onClick={() => camara.current?.click()}>
                  Sacar foto
                </button>
              )}
              <button type="button" className="hrr-btn" disabled={!!subiendo || ocupado}
                      onClick={() => galeria.current?.click()}>
                {subiendo
                  ? `Subiendo ${subiendo.hechas} de ${subiendo.total}…`
                  : vivas ? "Agregar más fotos" : "Agregar fotos"}
              </button>
            </>
          )}
          <input ref={galeria} type="file" accept="image/*" multiple hidden
                 aria-label="Agregar fotos" onChange={elegidas} />
          <input ref={camara} type="file" accept="image/*" capture="environment" hidden
                 aria-label="Sacar foto" onChange={elegidas} />
        </div>
      )}

      {error && <p className="hrr-aviso" role="alert">{error}</p>}
      {aviso && <p className="hrr-aviso hrr-aviso-wa" role="status">{aviso}</p>}

      {abierta !== null && lista[abierta] && (
        <FotoGrande
          fotos={lista}
          indice={abierta}
          onIndice={setAbierta}
          onCerrar={() => setAbierta(null)}
          rotulo={rotulo}
          acciones={editable ? (foto) => (alta ? (
            <button type="button" className="hrr-btn hrr-btn--chico" disabled={ocupado}
                    onClick={() => quitar(foto)}>
              Sacar de la lista
            </button>
          ) : (
            /* `key`: al pasar a otra foto la pregunta se cierra. Sin eso
               quedaba abierta sobre la siguiente, y un «Sí» rápido quitaba
               una que nadie eligió. */
            <BotonConfirmar key={foto.id} className="hrr-btn hrr-btn--chico"
                            disabled={ocupado}
                            pregunta="¿Quitarla?" si="Sí, quitar"
                            onConfirmar={() => quitar(foto)}>
              Quitar esta foto
            </BotonConfirmar>
          )) : null}
        />
      )}
    </section>
  );
}
